import { useEffect, useState, type FormEvent } from "react";
import {
  APP_SETTING_LABELS,
  appSettingsSchema,
  BORDER_PASS_RULE_LABELS,
  BORDER_PASS_RULES,
  DELIVERY_NOTIFY_SCOPE_LABELS,
  DELIVERY_NOTIFY_SCOPES,
  type AppSettingKey,
  type AppSettings,
  type BorderPassRule,
  type DeliveryNotifyScope,
} from "@omts/shared";
import { adminApi, fieldErrorsOf } from "../../api";
import { Field, errMsg } from "./common";

/** Числовые поля — строками, как в инпутах; приводятся при отправке. */
type FormState = {
  min_step: string;
  auto_extend_minutes: string;
  auto_extend_max: string;
  border_pass_rule: BorderPassRule;
  delivery_notify_scope: DeliveryNotifyScope;
  payment_due_days: string;
  payment_reminder_days: string;
};
type Errors = Partial<Record<AppSettingKey | "form", string>>;

const NUMERIC: readonly AppSettingKey[] = ["min_step", "auto_extend_minutes", "auto_extend_max", "payment_due_days", "payment_reminder_days"];

const fromSettings = (s: AppSettings): FormState => ({
  min_step: String(s.min_step),
  auto_extend_minutes: String(s.auto_extend_minutes),
  auto_extend_max: String(s.auto_extend_max),
  border_pass_rule: s.border_pass_rule,
  delivery_notify_scope: s.delivery_notify_scope,
  payment_due_days: String(s.payment_due_days),
  payment_reminder_days: String(s.payment_reminder_days),
});

const toNumber = (s: string): number => (s.trim() === "" ? Number.NaN : Number(s.trim()));

/** Сообщения zod по-русски для числовых диапазонов. */
function issueText(issue: { code: string; message: string; minimum?: unknown; maximum?: unknown }): string {
  if (issue.code === "invalid_type") return "Введите число";
  if (issue.code === "too_small") return `Не меньше ${String(issue.minimum)}`;
  if (issue.code === "too_big") return `Не больше ${String(issue.maximum)}`;
  if (issue.code === "invalid_enum_value") return "Выберите значение из списка";
  return issue.message;
}

/** Вкладка «Настройки» (16.4): все значения одной формой, каждое изменение попадает в журнал. */
export function SettingsTab() {
  const [saved, setSaved] = useState<AppSettings | null>(null);
  const [form, setForm] = useState<FormState | null>(null);
  const [errors, setErrors] = useState<Errors>({});
  const [busy, setBusy] = useState(false);
  const [notice, setNotice] = useState<string | null>(null);
  const [loadError, setLoadError] = useState<string | null>(null);

  useEffect(() => {
    adminApi
      .settings()
      .then((s) => {
        setSaved(s);
        setForm(fromSettings(s));
      })
      .catch((e: unknown) => setLoadError(errMsg(e)));
  }, []);

  const set = <K extends keyof FormState>(key: K, value: FormState[K]) => {
    setForm((f) => (f ? { ...f, [key]: value } : f));
    setErrors((e) => (e[key] ? { ...e, [key]: undefined } : e));
    setNotice(null);
  };

  const submit = async (e: FormEvent) => {
    e.preventDefault();
    if (!form) return;
    const candidate = {
      ...form,
      min_step: toNumber(form.min_step),
      auto_extend_minutes: toNumber(form.auto_extend_minutes),
      auto_extend_max: toNumber(form.auto_extend_max),
      payment_due_days: toNumber(form.payment_due_days),
      payment_reminder_days: toNumber(form.payment_reminder_days),
    };
    const parsed = appSettingsSchema.safeParse(candidate);
    if (!parsed.success) {
      const v: Errors = {};
      for (const i of parsed.error.issues) {
        const key = (i.path[0] ?? "form") as keyof Errors;
        if (!v[key]) v[key] = issueText(i);
      }
      return setErrors(v);
    }
    setBusy(true);
    setErrors({});
    try {
      const { settings, changed } = await adminApi.updateSettings(parsed.data);
      setSaved(settings);
      setForm(fromSettings(settings));
      setNotice(
        changed.length === 0
          ? "Изменений нет"
          : `Сохранено: ${changed.map((k) => APP_SETTING_LABELS[k]).join(", ")}. Записано в журнал.`,
      );
    } catch (err) {
      setErrors({ ...fieldErrorsOf(err), form: errMsg(err) });
    } finally {
      setBusy(false);
    }
  };

  if (loadError) return <p className="error">{loadError}</p>;
  if (!form || !saved) return <p className="muted">Загрузка…</p>;

  const dirty = JSON.stringify(form) !== JSON.stringify(fromSettings(saved));
  const numberInput = (key: (typeof NUMERIC)[number], props: { min: number; max: number; step?: number }) => (
    <input type="number" inputMode="numeric" value={form[key]} onChange={(e) => set(key, e.target.value)} {...props} />
  );

  return (
    <form className="lot-form" noValidate onSubmit={(e) => void submit(e)}>
      <section className="card">
        <h3>Торги</h3>
        <div className="form-grid">
          <Field label={APP_SETTING_LABELS.min_step} required error={errors.min_step} hint="Снимается в заявку при создании; на уже созданные не влияет">
            {numberInput("min_step", { min: 1, max: 100_000_000, step: 1000 })}
          </Field>
          <div />
          <Field label={APP_SETTING_LABELS.auto_extend_minutes} required error={errors.auto_extend_minutes} hint="Ставка менее чем за N минут до дедлайна продлевает торги на N минут">
            {numberInput("auto_extend_minutes", { min: 1, max: 1440 })}
          </Field>
          <Field label={APP_SETTING_LABELS.auto_extend_max} required error={errors.auto_extend_max} hint="0 — автопродление отключено даже если включено в заявке">
            {numberInput("auto_extend_max", { min: 0, max: 20 })}
          </Field>
        </div>
      </section>

      <section className="card">
        <h3>Доставка</h3>
        <div className="form-grid">
          <Field label={APP_SETTING_LABELS.border_pass_rule} required error={errors.border_pass_rule} hint="Когда без талона нельзя подтвердить доставку">
            <select value={form.border_pass_rule} onChange={(e) => set("border_pass_rule", e.target.value as BorderPassRule)}>
              {BORDER_PASS_RULES.map((r) => (
                <option key={r} value={r}>
                  {BORDER_PASS_RULE_LABELS[r]}
                </option>
              ))}
            </select>
          </Field>
          <Field label={APP_SETTING_LABELS.delivery_notify_scope} required error={errors.delivery_notify_scope} hint="Кому приходит «перевозчик сообщил о доставке»">
            <select value={form.delivery_notify_scope} onChange={(e) => set("delivery_notify_scope", e.target.value as DeliveryNotifyScope)}>
              {DELIVERY_NOTIFY_SCOPES.map((s) => (
                <option key={s} value={s}>
                  {DELIVERY_NOTIFY_SCOPE_LABELS[s]}
                </option>
              ))}
            </select>
          </Field>
        </div>
      </section>

      <section className="card">
        <h3>Оплата</h3>
        <div className="form-grid">
          <Field label={APP_SETTING_LABELS.payment_due_days} required error={errors.payment_due_days} hint="Календарных дней от подтверждения доставки; действует на новые подтверждения">
            {numberInput("payment_due_days", { min: 1, max: 365 })}
          </Field>
          <Field label={APP_SETTING_LABELS.payment_reminder_days} required error={errors.payment_reminder_days} hint="Напоминание автору заявки и администраторам; при просрочке — каждый день">
            {numberInput("payment_reminder_days", { min: 1, max: 60 })}
          </Field>
        </div>
      </section>

      {errors.form && <p className="error">{errors.form}</p>}
      {notice && <p className="notice">{notice}</p>}
      <div className="form-actions">
        <button type="submit" className="btn primary" disabled={busy || !dirty}>
          {busy ? "Сохраняем…" : "Сохранить"}
        </button>
        {dirty && (
          <button type="button" className="btn" disabled={busy} onClick={() => setForm(fromSettings(saved))}>
            Отменить изменения
          </button>
        )}
        <span className="muted">Каждое изменение записывается в журнал с прежним и новым значением</span>
      </div>
    </form>
  );
}
